// ChatSidebar.tsx
import React, { useState } from 'react';
import { Offcanvas, Nav, ListGroup, Button, Form, Image } from 'react-bootstrap';
import { X, ArrowLeft, ChatDots } from 'react-bootstrap-icons';

interface ChatMessage {
  id: number;
  sender: 'me' | 'them';
  text: string;
}

interface MessageUser {
  id: number;
  name: string;
  category: 'messages';
  lastMessage: string;
  messages: ChatMessage[];
}

interface QuestionUser {
  id: number;
  name: string;
  category: 'questions';
  lastQuestion: string;
  questionAbout: 'profile' | 'prompt' | 'portfolio';
  questionAboutId: string | null;
  messages: ChatMessage[];
}

type ChatUser = MessageUser | QuestionUser;

const demoUsers: ChatUser[] = [
  {
    id: 1,
    name: 'Justin',
    category: 'messages',
    lastMessage: 'Appreciate the business...',
    messages: [
      {
        id: 1,
        sender: 'them',
        text: 'other job I had..took longer than expected',
      },
      { id: 2, sender: 'them', text: 'May be a little late' },
      { id: 3, sender: 'me', text: 'No worries' },
    ],
  },
  {
    id: 2,
    name: 'Sarah',
    category: 'questions',
    lastQuestion: 'Are we still on for tomorrow?',
    questionAbout: 'prompt',
    questionAboutId: 'prompt-42',
    messages: [
      { id: 1, sender: 'them', text: 'Are we still on for tomorrow?' },
      { id: 2, sender: 'me', text: 'Yes, confirmed!' },
      { id: 3, sender: 'them', text: 'See you tomorrow!' },
    ],
  },
  {
    id: 3,
    name: 'David',
    category: 'questions',
    lastQuestion: 'Can you share your portfolio?',
    questionAbout: 'portfolio',
    questionAboutId: null,
    messages: [
      { id: 1, sender: 'them', text: 'Can you share your portfolio?' },
      { id: 2, sender: 'me', text: 'Sure, sending it now.' },
    ],
  },
  {
    id: 4,
    name: 'Jake',
    category: 'messages',
    lastMessage: 'Appreciate the craft...',
    messages: [
      {
        id: 1,
        sender: 'them',
        text: 'other job I had..took longer than expected',
      },
      { id: 2, sender: 'them', text: 'May be a little late' },
      { id: 3, sender: 'me', text: 'No worries' },
    ],
  },
];

// Build contact list dynamically
const buildContacts = (): MessageUser[] => {
  const seen = new Map<number, ChatUser>();
  demoUsers.forEach((u) => seen.set(u.id, u));
  return Array.from(seen.values()).map((u) => ({
    id: u.id,
    name: u.name,
    category: 'messages', // treat as contact
    lastMessage: u.category === 'messages' ? u.lastMessage : (u as QuestionUser).lastQuestion,
    messages: u.messages,
  }));
};

const ChatSidebar: React.FC<{ show: boolean; handleClose: () => void }> = ({
  show,
  handleClose,
}) => {
  const [activeCategory, setActiveCategory] = useState<'questions' | 'messages' | 'contacts'>(
    'messages'
  );
  const [activeUser, setActiveUser] = useState<ChatUser | null>(null);
  const [viewProfile, setViewProfile] = useState(false); // only for contacts
  const [reply, setReply] = useState('');

  const filteredUsers =
    activeCategory === 'contacts'
      ? buildContacts()
      : demoUsers.filter((u) => u.category === activeCategory);

  const sendReply = () => {
    if (reply.trim() && activeUser) {
      activeUser.messages.push({ id: Date.now(), sender: 'me', text: reply });
      setReply('');
    }
  };

  return (
    <Offcanvas show={show} onHide={handleClose} placement="end" style={{ width: '350px' }}>
      {/* Header */}
      <div
        className="d-flex align-items-center p-3"
        style={{ backgroundColor: '#0f5132', color: 'white' }}
      >
        {activeUser || viewProfile ? (
          <Button
            variant="link"
            className="text-white me-2 p-0"
            onClick={() => {
              setActiveUser(null);
              setViewProfile(false);
            }}
          >
            <ArrowLeft size={20} />
          </Button>
        ) : (
          <span className="me-2 fw-bold">✔</span>
        )}
        <h5 className="mb-0">
          {activeUser ? activeUser.name : viewProfile ? 'Profile' : 'SkipScreen'}
        </h5>
        <Button variant="link" className="text-white ms-auto p-0" onClick={handleClose}>
          <X size={24} />
        </Button>
      </div>

      {/* Category Nav */}
      {!activeUser && !viewProfile && (
        <Nav
          fill
          variant="tabs"
          activeKey={activeCategory}
          onSelect={(k) => setActiveCategory(k as any)}
        >
          <Nav.Item>
            <Nav.Link eventKey="questions">Questions</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="messages">Messages</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="contacts">Contacts</Nav.Link>
          </Nav.Item>
        </Nav>
      )}

      <Offcanvas.Body className="d-flex flex-column p-0">
        {/* Chat List */}
        {!activeUser && !viewProfile && (
          <ListGroup variant="flush" style={{ flex: 1, overflowY: 'auto' }}>
            {filteredUsers.map((user) => (
              <ListGroup.Item
                key={user.id}
                action
                onClick={() => {
                  if (activeCategory === 'contacts') {
                    setActiveUser(user); // set for later chat
                    setViewProfile(true); // show mini profile
                  } else {
                    setActiveUser(user); // go straight to chat
                  }
                }}
              >
                <div className="fw-bold">{user.name}</div>
                <small className="text-muted">
                  {user.category === 'messages'
                    ? (user as MessageUser).lastMessage
                    : (user as QuestionUser).lastQuestion}
                </small>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}

        {/* Mini Profile (contacts) */}
        {viewProfile && activeUser && (
          <div className="text-center mt-5">
            <Image
              src={`https://i.pravatar.cc/150?img=${activeUser.id}`} // fake avatar
              roundedCircle
              width={100}
              height={100}
              className="mb-3"
            />
            <h5>{activeUser.name}</h5>
            <Button
              variant="primary"
              className="mt-3"
              onClick={() => setViewProfile(false)} // switch to chat
            >
              <ChatDots size={18} className="me-2" />
              Open Chat
            </Button>
          </div>
        )}

        {/* Chat Messages */}
        {activeUser && !viewProfile && (
          <div className="d-flex flex-column" style={{ flex: 1 }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
              {activeUser.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`mb-2 d-flex ${
                    msg.sender === 'me' ? 'justify-content-end' : 'justify-content-start'
                  }`}
                >
                  <div
                    className="p-2 rounded"
                    style={{
                      maxWidth: '75%',
                      background: msg.sender === 'me' ? '#e9f7ef' : '#f1f1f1',
                    }}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-top p-2 d-flex">
              <Form.Control
                type="text"
                placeholder="Type a reply..."
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <Button className="ms-2" onClick={sendReply}>
                Send
              </Button>
            </div>
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default ChatSidebar;
